import { useState, useMemo } from 'react';
import {
  Modal,
  TextInput,
  Select,
  SelectItem,
  Tag,
  Stack
} from '@carbon/react';
import { Search, ArrowRight, Checkmark } from '@carbon/icons-react';
import { Node, Edge } from '@xyflow/react';
import { ConceptNodeData } from './ConceptNode';

interface ConceptLinkerProps {
  isOpen: boolean;
  onClose: () => void;
  sourceConcept: Node | null;
  availableConcepts: Node[];
  existingEdges: Edge[];
  onCreateLink: (sourceId: string, targetId: string, relationship: string) => void;
}

const relationshipTypes = [
  { value: 'includes', label: 'includes' },
  { value: 'is_a', label: 'is a type of' },
  { value: 'uses', label: 'uses' },
  { value: 'requires', label: 'requires' },
  { value: 'related_to', label: 'related to' },
  { value: 'applies_to', label: 'applies to' },
  { value: 'part_of', label: 'part of' }
];

const suggestRelationship = (source: ConceptNodeData, target: ConceptNodeData): string => {
  // Simple AI-like relationship suggestion based on concept names
  const sourceLabel = source.label.toLowerCase();
  const targetLabel = target.label.toLowerCase();
  
  // ML/AI domain knowledge
  if (sourceLabel.includes('artificial intelligence') && 
      (targetLabel.includes('machine learning') || targetLabel.includes('deep learning'))) {
    return 'includes';
  }
  
  if (sourceLabel.includes('machine learning') && targetLabel.includes('neural network')) {
    return 'uses';
  }
  
  if (targetLabel.includes('data') && sourceLabel.includes('learning')) {
    return 'requires';
  }
  
  // Default suggestion
  return 'related_to';
};

export const ConceptLinker = ({ 
  isOpen, 
  onClose, 
  sourceConcept, 
  availableConcepts, 
  existingEdges,
  onCreateLink 
}: ConceptLinkerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRelationship, setSelectedRelationship] = useState<string>('');
  const [selectedTarget, setSelectedTarget] = useState<Node | null>(null);

  const sourceData = sourceConcept?.data as ConceptNodeData;

  // Filter concepts that aren't already connected and match search
  const filteredConcepts = useMemo(() => {
    if (!sourceConcept) return [];
    
    const connectedIds = new Set(
      existingEdges
        .filter(edge => edge.source === sourceConcept.id || edge.target === sourceConcept.id)
        .map(edge => edge.source === sourceConcept.id ? edge.target : edge.source)
    );
    
    return availableConcepts
      .filter(concept => 
        concept.id !== sourceConcept.id && 
        !connectedIds.has(concept.id) &&
        (concept.data as ConceptNodeData).label.toLowerCase().includes(searchQuery.toLowerCase())
      );
  }, [sourceConcept, availableConcepts, existingEdges, searchQuery]);

  const handleTargetSelect = (target: Node) => {
    setSelectedTarget(target);
    const targetData = target.data as ConceptNodeData;
    const suggestedRelation = suggestRelationship(sourceData, targetData);
    setSelectedRelationship(suggestedRelation);
  };

  const handleCreateLink = () => {
    if (sourceConcept && selectedTarget && selectedRelationship) {
      onCreateLink(sourceConcept.id, selectedTarget.id, selectedRelationship);
      setSelectedTarget(null);
      setSelectedRelationship('');
      setSearchQuery('');
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedTarget(null);
    setSelectedRelationship('');
    setSearchQuery('');
    onClose();
  };

  if (!sourceConcept) return null;

  return (
    <Modal
      open={isOpen}
      onRequestClose={handleClose}
      modalHeading={`Link "${sourceData.label}"`}
      modalLabel="Concept Linker"
      primaryButtonText="Create Link"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleCreateLink}
      primaryButtonDisabled={!selectedTarget || !selectedRelationship}
      size="lg"
    >
      <Stack gap={6}>
        <p style={{ 
          fontSize: '0.875rem', 
          color: 'var(--cds-text-secondary)',
          marginBottom: '1rem'
        }}>
          Search for concepts to connect and define their relationship. AI suggests the most likely relationship type.
        </p>

        {/* Search Input */}
        <div style={{ position: 'relative' }}>
          <TextInput
            id="search-concepts"
            labelText="Search concepts"
            placeholder="Search concepts to link..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Search
            size={16}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '2.5rem',
              fill: 'var(--cds-icon-secondary)'
            }}
          />
        </div>

        {/* Available Concepts */}
        <div style={{ 
          maxHeight: '300px', 
          overflowY: 'auto',
          border: '1px solid var(--cds-border-subtle-01)',
          borderRadius: '4px',
          padding: '0.5rem'
        }}>
          {filteredConcepts.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              color: 'var(--cds-text-secondary)',
              padding: '2rem'
            }}>
              {searchQuery ? 'No matching concepts found' : 'All available concepts are already connected'}
            </div>
          ) : (
            <Stack gap={3}>
              {filteredConcepts.map((concept) => {
                const conceptData = concept.data as ConceptNodeData;
                const isSelected = selectedTarget?.id === concept.id;
                
                return (
                  <div
                    key={concept.id}
                    onClick={() => handleTargetSelect(concept)}
                    style={{
                      padding: '0.75rem',
                      border: isSelected 
                        ? '2px solid var(--cds-focus)' 
                        : '1px solid var(--cds-border-subtle-01)',
                      borderRadius: '4px',
                      backgroundColor: isSelected 
                        ? 'var(--cds-layer-selected-01)' 
                        : 'var(--cds-layer-01)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'var(--cds-layer-hover-01)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = 'var(--cds-layer-01)';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          fontSize: '0.875rem', 
                          fontWeight: 600,
                          marginBottom: '0.25rem',
                          color: 'var(--cds-text-primary)'
                        }}>
                          {conceptData.label}
                        </h4>
                        <p style={{ 
                          fontSize: '0.75rem', 
                          color: 'var(--cds-text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {conceptData.definition}
                        </p>
                      </div>
                      {isSelected && (
                        <Checkmark size={20} style={{ fill: 'var(--cds-interactive-01)' }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </Stack>
          )}
        </div>

        {/* Selected Connection Preview */}
        {selectedTarget && (
          <div style={{
            border: '1px solid var(--cds-border-subtle-01)',
            borderRadius: '4px',
            padding: '1rem',
            backgroundColor: 'var(--cds-layer-02)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              marginBottom: '1rem'
            }}>
              <Tag type="blue">{sourceData.label}</Tag>
              <ArrowRight size={16} />
              <Tag type="green">{(selectedTarget.data as ConceptNodeData).label}</Tag>
            </div>
            
            <Select
              id="relationship-type"
              labelText="Relationship Type"
              value={selectedRelationship}
              onChange={(e) => setSelectedRelationship(e.target.value)}
            >
              <SelectItem value="" text="Select relationship type" />
              {relationshipTypes.map((type) => (
                <SelectItem 
                  key={type.value} 
                  value={type.value} 
                  text={type.label}
                />
              ))}
            </Select>
          </div>
        )}
      </Stack>
    </Modal>
  );
};

// Made with Bob
